// Cmd-K / Ctrl-K command palette. Opens from anywhere, hits /api/search,
// shows contacts + jobs + invoices + estimates ranked by recency.
// Enter selects, arrow keys move, escape closes.
import React, { useEffect, useState, useRef } from 'react';
import { G, styles, api } from '../theme.js';

const SECTION_LABEL = { contacts:'Contacts', jobs:'Jobs', invoices:'Invoices', estimates:'Estimates' };
const SECTION_ICON  = { contacts:'👤', jobs:'🔧', invoices:'💰', estimates:'📄' };

export function CmdK({ go }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ contacts:[], jobs:[], invoices:[], estimates:[] });
  const [idx, setIdx] = useState(0);
  const inputRef = useRef(null);

  // Global keybind — Cmd-K / Ctrl-K opens. Escape closes.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Focus + clear when opened
  useEffect(() => {
    if (open) {
      setQ(''); setResults({ contacts:[], jobs:[], invoices:[], estimates:[] }); setIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (q.length < 2) { setResults({ contacts:[], jobs:[], invoices:[], estimates:[] }); return; }
    const t = setTimeout(() => {
      api(`/api/search?q=${encodeURIComponent(q)}`).then(setResults).catch(()=>{});
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  // Flatten into a single array so arrow-keys + enter just work.
  const flat = [];
  for (const section of ['contacts','jobs','invoices','estimates']) {
    for (const item of (results[section] || [])) flat.push({ section, item });
  }

  const selectIndex = (i) => {
    const entry = flat[i];
    if (!entry) return;
    setOpen(false);
    if (entry.section === 'contacts') {
      window.dispatchEvent(new CustomEvent('tradeops:open-contact', { detail: { contactId: entry.item.id } }));
    } else if (entry.section === 'jobs') {
      go && go('jobs');
      setTimeout(() => window.dispatchEvent(new CustomEvent('tradeops:open-job', { detail: { jobId: entry.item.id } })), 100);
    } else if (entry.section === 'invoices') {
      go && go('invoices');
    } else if (entry.section === 'estimates') {
      go && go('estimates');
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(flat.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx(i => Math.max(0, i - 1)); }
    else if (e.key === 'Enter')   { e.preventDefault(); selectIndex(idx); }
  };

  if (!open) return null;

  let runningIdx = 0;
  return (
    <div onClick={()=>setOpen(false)} style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,.55)', backdropFilter:'blur(4px)',
      zIndex:6000, display:'flex', alignItems:'flex-start', justifyContent:'center', paddingTop:'10vh',
    }}>
      <div onClick={e=>e.stopPropagation()} style={{
        ...styles.card, width:'min(640px, 92vw)', padding:0, overflow:'hidden',
        boxShadow:'0 24px 64px rgba(0,0,0,.45)', border:`1px solid ${G.cardBorder}`,
      }}>
        <input ref={inputRef} value={q} onChange={e=>{ setQ(e.target.value); setIdx(0); }} onKeyDown={onKeyDown}
          placeholder="Search contacts, jobs, invoices, estimates… (Cmd-K)"
          style={{
            width:'100%', padding:'18px 20px', background:'transparent', color:G.text, fontSize:16,
            border:'none', borderBottom:`1px solid ${G.border}`, outline:'none',
          }}/>
        <div style={{ maxHeight:'60vh', overflowY:'auto' }}>
          {q.length < 2 && (
            <div style={{ padding:24, color:G.textMuted, fontSize:13, textAlign:'center' }}>
              Type to search. Tip: ⌘K opens this from anywhere.
            </div>
          )}
          {q.length >= 2 && flat.length === 0 && (
            <div style={{ padding:24, color:G.textMuted, fontSize:13, textAlign:'center' }}>No matches.</div>
          )}
          {['contacts','jobs','invoices','estimates'].map(section => {
            const items = results[section] || [];
            if (!items.length) return null;
            return (
              <div key={section}>
                <div style={{ padding:'8px 16px', fontSize:10, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.08em', background:G.input }}>
                  {SECTION_ICON[section]} {SECTION_LABEL[section]}
                </div>
                {items.map(item => {
                  const myIdx = runningIdx++;
                  const active = myIdx === idx;
                  return (
                    <div key={item.id}
                      onMouseEnter={()=>setIdx(myIdx)}
                      onClick={()=>selectIndex(myIdx)}
                      style={{
                        padding:'10px 16px', cursor:'pointer', fontSize:13,
                        background: active ? G.purpleDim : 'transparent',
                        borderLeft: active ? `3px solid ${G.purple}` : '3px solid transparent',
                        display:'flex', justifyContent:'space-between', gap:14,
                      }}>
                      <div style={{ flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis' }}>
                        <div style={{ fontWeight:600 }}>
                          {section === 'contacts' && `${item.firstName||''} ${item.lastName||''}`.trim()}
                          {section === 'jobs' && (item.name || 'Job')}
                          {section === 'invoices' && `Invoice ${item.num}`}
                          {section === 'estimates' && `Estimate ${item.num}${item.title?` · ${item.title}`:''}`}
                        </div>
                        <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>
                          {section === 'contacts' && `${item.phone||''}${item.email?` · ${item.email}`:''}${item.stage?` · ${item.stage}`:''}`}
                          {section === 'jobs' && `${item.contactName||''}${item.status?` · ${item.status}`:''}${item.scheduledDate?` · ${item.scheduledDate}`:''}`}
                          {section === 'invoices' && `${item.contactName||''} · $${Number(item.total||0).toFixed(2)} · ${item.status}`}
                          {section === 'estimates' && `${item.contactName||''} · ${item.status}`}
                        </div>
                      </div>
                      {active && <span style={{ color:G.purple, fontSize:11, fontWeight:700, alignSelf:'center' }}>↵</span>}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
        <div style={{ padding:'8px 16px', fontSize:10, color:G.textMuted, borderTop:`1px solid ${G.border}`, display:'flex', justifyContent:'space-between' }}>
          <span>↑↓ navigate · ↵ select · esc close</span>
          <span>⌘K to open anywhere</span>
        </div>
      </div>
    </div>
  );
}
